import { useState, useRef, useEffect } from 'react';
import {
  DataGridPro,
  GridApi,
  GridRowParams,
  GridColumnResizeParams,
  GridSortModel,
  GridSelectionModel
} from '@mui/x-data-grid-pro';
import { GridUtilities } from './components/gridUtilities/GridUtilities';
import { IGrid } from './interface';
import { queryBuilder } from './query-builder.service';
import { ManageNotData } from '../manageNotData/ManageNotData';
import { AppLoading } from '../appLoading/appLoading';
import { useSelector, useDispatch } from 'react-redux';
import {
  addNextPageRows,
  getRows,
  getNextPage,
  sort,
  inputSearch,
  addFilter,
  setQueryString,
  resetCount,
  setFields,
  clearAllFilters,
  setClusterType,
  setClusterSort,
  resetNext,
  resetShowUpdatedFields
} from 'features/GridSlice';
import { urlToStateConverter } from 'common/services/UrlToStateConverter.service';
import { ColumnsManage } from './columnsManage';
import { SavedViews } from './components/SavedViews/SavedViews';
import InfoPanel from './components/InfoPanel/InfoPanel';
import { RowCount } from './components/rowCount/RowCount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';
import { Text } from '@cyberpion/cyberpion-ui';
import { IReduxState } from 'app/store';
import { setGlobalFilter } from 'features/GeneralSlice';
import { selectView, setIsEdited, setIsOpenSaveViews } from 'features/ViewsSlice';
import { resetInfoPanel } from 'features/InfoPanelSlice';
import { Common } from 'common/services/common.service';
import { styled } from '@mui/material/styles';
import { ClusterGrid } from '../ClusterGrid/ClusterGrid';
import { setHistoryData, setHistoryUrl } from 'features/historySlice';

import './grid.scss';

let PROMISE: any;

export const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    fontSize: '11px !important',
    color: '#8c909a !important'
  },
  '& .MuiDataGrid-cellCheckbox': {
    '& .MuiSvgIcon-root': {
      width: '18px !important',
      height: '18px !important'
    }
  },
  '& .MuiDataGrid-columnHeader': {
    '& .MuiSvgIcon-root': {
      width: '18px !important',
      height: '18px !important',
      color: '#8C909A90'
    },
    '& .Mui-checked > .MuiSvgIcon-root': {
      color: '#4A79E7'
    }
  },
  '& .MuiDataGrid-row': {
    backgroundColor: '#FFFFFF !important',

    '.MuiCheckbox-root > .MuiSvgIcon-root': {
      color: '#8C909A90'
    },
    '.grid-link-field, .Comments, .info-panel-count, a > .grade-render-scope': {
      color: '#4A79E7',
      padding: '4px 6px',
      borderRadius: 4,
      backgroundColor: '#F9FAFF !important'
    },

    '&:hover': {
      backgroundColor: '#F7F7F7 !important',

      '& .MuiDataGrid-cell': {
        '.grid-link-field, .Comments, .info-panel-count, a > .grade-render-scope': {
          backgroundColor: '#F1F1F1 !important',
          '&:hover': {
            backgroundColor: '#F0ECEC !important'
          }
        },
        '& .MuiCheckbox-root': {
          // color: 'blue',
          '&:hover': {
            '& .MuiSvgIcon-root': {
              color: '#5B76D5'
            }
          }
        }
      }
    },

    '&.Mui-selected': {
      backgroundColor: '#F5F6FF !important',

      '.MuiCheckbox-root > .MuiSvgIcon-root': {
        color: '#1876D2'
      },
      '&:hover': {
        backgroundColor: '#ECEFFE !important'
      },
      '& .MuiDataGrid-cell': {
        '.grid-link-field, .Comments, .info-panel-count, a > .grade-render-scope': {
          backgroundColor: '#E2E3FD !important',
          '&:hover': {
            backgroundColor: '#D6D7FB !important'
          }
        }
      }
    }
  }
}));

export function Grid(props: IGrid) {
  const apiRef: any = useRef<GridApi>();
  const [updatedColumns, setUpdatedColumns] = useState(props.columns);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [csvDownloadPath, setCsvDownloadPath] = useState<null | string>('');
  const [localSearchTerm, setLocalSearchTerm] = useState<string>('');
  const [filteredRows, setFilteredRows] = useState<any[]>([]);
  const {
    rows,
    loading,
    nextPageRows,
    nextPath,
    count,
    order,
    filters,
    search,
    fields,
    allowURLChange,
    capturedState,
    clusterType,
    clusterSort,
    investigation_rules,
    investigation_rules_operator,
    showUpdatedFields,
    filtersWithAndOperator
  } = useSelector((state: any) => state.grid);
  const { globalFilter } = useSelector((state: any) => state.general);
  const { viewSelected } = useSelector((state: any) => state.views);
  const { filtersResetDefault, initialPath } = useSelector((state: IReduxState) => state.grid);
  const { isOpenInfoPanel } = useSelector((state: IReduxState) => state.infoPanel);
  const { isShowSavedViews } = useSelector((state: any) => state.views);
  const { columnPicker } = useSelector((state: IReduxState) => state.columnPicker);
  const { historyData } = useSelector((state: IReduxState) => state.history);

  const location = useLocation();
  const dispatch = useDispatch<any>();
  let mouseUpEventTimer: ReturnType<typeof setTimeout>;

  const flags = useFlags();

  const setMouseUpEvent = (): void => {
    mouseUpEventTimer = setTimeout(() => {
      setIsResizing(false);
      clearTimeout(mouseUpEventTimer);
    }, 100);
  };

  const getContextAndSubContext = () => {
    let context;
    let subContext;
    if (!!props.page) {
      context = props.page;
      subContext = !!props.pageTab ? props.pageTab : 'default';
    } else {
      const path = props.path?.split('/');
      const page = path?.[path.length - 1] === '' ? path?.[path.length - 2] : path?.[path.length - 1];
      context = 'settings';
      subContext = page;
    }
    return { context, subContext };
  };

  const setColumnsOrderFromHistory = (context: string, subContext: string) => {
    const seenFields = historyData[context][subContext];
    const updatedFields = seenFields.fields.reduce((acc: any, item: any) => {
      const field = props.columns.find((col: any) => col.field === item);
      return field ? [...acc, { ...field, hide: false, is_toggled: true }] : acc;
    }, []);

    setUpdatedColumns(updatedFields);
  };

  const setColumnsOrderFromSaveViews = (_fields: any) => {
    const updatedFields = _fields.reduce((acc: any, item: any) => {
      const field = props.columns.find((col: any) => col.field === item);
      return field ? [...acc, { ...field, hide: false, is_toggled: true }] : acc;
    }, []);

    setUpdatedColumns(updatedFields);
  };

  const handleUrlChange = () => {
    const { context, subContext } = getContextAndSubContext();

    const search = urlToStateConverter.getSearch();
    const order = urlToStateConverter.getSort();
    const filters = urlToStateConverter.getFilters();
    const fields = urlToStateConverter.getFields();
    const global_filter = urlToStateConverter.getGlobalFilter();
    const clusterType = urlToStateConverter.getClusterType();
    const clusterSort = urlToStateConverter.getClusterSort();
    if (global_filter) {
      dispatch(setGlobalFilter(global_filter));
    }
    dispatch(setClusterType(clusterType));
    dispatch(setClusterSort(clusterSort));
    dispatch(inputSearch(search));
    if (fields) {
      let _fields = Array.isArray(fields) ? fields : fields.split(',');

      dispatch(setFields(_fields));

      ColumnsManage.hideColumns(apiRef, updatedColumns, _fields);
      ColumnsManage.showSpecificColumns(apiRef, updatedColumns, _fields);
    }
    dispatch(sort(order));

    if (Object.keys(filters).length) {
      for (let filter in filters) {
        dispatch(addFilter({ [filter]: filters[filter] }));
      }
    } else {
      dispatch(clearAllFilters());
    }

    if (!!flags.shouldAccountUseHistoryData) {
      const queryParams = new URLSearchParams(location.search);
      const isSavedViews = queryParams.has('is_saved_view') && queryParams.get('is_saved_view') === 'true';

      if (isSavedViews) {
        const viewFields = queryParams.has('fields') ? queryParams.get('fields') : '';
        dispatch(setFields(viewFields?.split(',')));
        dispatch(
          setHistoryData({
            context,
            subContext,
            data: { url: window.location.href.replace('&is_saved_view=true', ''), fields: viewFields?.split(',') }
          })
        );

        setColumnsOrderFromSaveViews(viewFields?.split(','));

        // WILL NOT TRIGGER THE URL CHANGE (THIS FUNCTION) AGAIN
        const url = new URL(window.location.href);
        url.searchParams.delete('is_saved_view');
        window.history.replaceState({}, '', url);
      }
      if (!isSavedViews && context && subContext && !!historyData[context] && !!historyData[context][subContext]) {
        dispatch(setHistoryUrl({ context, subContext, url: window.location.href.replace('&is_saved_view=true', '') }));
        setColumnsOrderFromHistory(context, subContext);
        dispatch(setFields(historyData[context][subContext].fields));
      }
    }
  };

  useEffect(() => {
    if (!allowURLChange) {
      return;
    }
    handleUrlChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, props.path]);

  useEffect(() => {
    if (viewSelected || filtersResetDefault) {
      handleUrlChange();
      dispatch(getRows());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (!allowURLChange) {
      return;
    }

    urlToStateConverter.setQueryString(
      filters,
      search,
      order,
      globalFilter,
      fields,
      clusterType,
      clusterSort,
      investigation_rules,
      investigation_rules_operator,
      filtersWithAndOperator
    );

    dispatch(setQueryString({ tab: props.pageTab, qs: window.location.search }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    fields,
    search,
    order,
    globalFilter,
    clusterType,
    clusterSort,
    dispatch,
    investigation_rules,
    investigation_rules_operator,
    filtersWithAndOperator
  ]);

  useEffect(() => {
    if (PROMISE) {
      PROMISE.abort();
    }
    dispatch(resetCount());
    dispatch(resetInfoPanel());
    dispatch(setIsOpenSaveViews(false));
    dispatch(setIsEdited(false));
    PROMISE = dispatch(getRows());
  }, [dispatch, props.path]);

  useEffect(() => {
    if (!!nextPath) {
      dispatch(getNextPage());
    } else {
      dispatch(resetNext());
    }
  }, [dispatch, nextPath]);

  useEffect(() => {
    if (rows.length) {
      let _fields: any;
      const { context, subContext } = getContextAndSubContext();
      if (
        context &&
        subContext &&
        historyData[context] &&
        historyData[context][subContext] &&
        historyData[context][subContext].fields
      ) {
        _fields = historyData[context][subContext].fields;
        queryBuilder.buildQuery({ fields: _fields });
      }

      const currentQuery = queryBuilder.getQuery();
      const newQuery = buildQuery(currentQuery);
      const csvAPI = newQuery.split('api/v1/')[1];

      if (selectionModel.length < 100) {
        setCsvDownloadPath(prepareCSVDownloadPath(csvAPI + `&${props.identifier}__in=${selectionModel.join(',')}`));
      } else {
        setCsvDownloadPath(prepareCSVDownloadPath(csvAPI));
      }
      PROMISE = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectionModel, props.identifier, clusterType, clusterSort, fields]);

  useEffect(() => {
    setSelectionModel([]);
  }, [clusterType]);

  useEffect(() => {
    document.addEventListener('mouseup', setMouseUpEvent);

    return () => {
      document.removeEventListener('mouseup', setMouseUpEvent);
      clearTimeout(mouseUpEventTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetInfoPanel());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!!showUpdatedFields?.length && !!columnPicker?.length) {
      const _columnsPicker = columnPicker.map((item: any) => {
        return { ...item, is_toggled: false };
      });
      const updatedColumns = showUpdatedFields.map((item: string) => {
        const column: any = _columnsPicker.find((col: any) => col.name === item);
        if (item === column.name) {
          const updatedColumn = {
            ...column,
            is_toggled: true
          };
          return updatedColumn;
        }
        return column;
      });

      let fields = ColumnsManage.checkGridColumnsToDisplay(updatedColumns);

      ColumnsManage.hideColumns(apiRef, updatedColumns, fields);
      dispatch(setFields(fields));
      dispatch(resetShowUpdatedFields());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpdatedFields, columnPicker]);

  // Local search functionality
  useEffect(() => {
    if (props.clientSideMode) {
      const currentRows = _getRows();
      if (localSearchTerm.trim() === '') {
        setFilteredRows(currentRows);
      } else {
        const filtered = currentRows.filter((row: any) => {
          return updatedColumns.some((column: any) => {
            const value = row[column.field];
            if (value === null || value === undefined) return false;
            return value.toString().toLowerCase().includes(localSearchTerm.toLowerCase());
          });
        });
        setFilteredRows(filtered);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearchTerm, rows, updatedColumns, props.clientSideMode]);

  const handleLocalSearch = (searchTerm: string) => {
    setLocalSearchTerm(searchTerm);
  };

  const getDisplayCount = () => {
    if (props.clientSideMode) {
      // Client-side mode: show filtered count when searching, total count when not searching
      return localSearchTerm.trim() !== '' ? filteredRows.length : count;
    }
    // Server-side mode: always show Redux count
    return count;
  };

  const buildQuery = (query: string) => {
    const _query = new URL(query);
    const fields = _query.searchParams.getAll('fields');

    if (clusterType) {
      if (['primary_ai', 'handle_together_group'].includes(clusterType)) {
        if (!fields.includes(clusterType)) {
          fields.push(clusterType);
        }
      }
      if (clusterSort) {
        _query.searchParams.set('ordering', clusterSort);
      }

      _query.searchParams.set('clusterBy', clusterType);
    }

    if (!fields.includes('id')) {
      fields.push('id');
    }

    _query.searchParams.set('fields', fields.join());

    return decodeURIComponent(_query.toString());
  };

  const handleOnRowsScrollEnd = () => {
    if (nextPageRows.length) {
      dispatch(addNextPageRows());
    }
  };

  const prepareCSVDownloadPath = (path: any): string | null => {
    const p = path.replace('/summary/', '/');
    return Common.AddMsspAccountIfNeeded(`${p}&format=csv`);
  };

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (isResizing) {
      return;
    }

    // Skip server-side sorting if client-side mode is enabled
    if (props.clientSideMode) {
      return;
    }

    if (model[0]) {
      const orderString = model[0].sort === 'asc' ? `-${model[0].field}` : model[0].field;
      if (order !== orderString) {
        dispatch(setIsEdited(true));
        dispatch(selectView(null));
        dispatch(sort(orderString));
        dispatch(getRows());
      }
    }
  };

  const handleOnColumnResize = (params: GridColumnResizeParams) => {
    setIsResizing(true);
  };

  const handleOnSelectionModelChange = (model: GridSelectionModel) => {
    if (model) {
      setSelectionModel(model);
    }
  };

  const _getRows = () => {
    if (props.isPopup) {
      return rows[0] && rows[0][props.identifier] ? rows : [];
    } else if (Object.keys(capturedState).length) {
      return capturedState.rows[0] && capturedState.rows[0][props.identifier] ? capturedState.rows : [];
    } else {
      return rows[0] && rows[0][props.identifier] ? rows : [];
    }
  };

  const getOrder = () => {
    const order = urlToStateConverter.getSort();
    if (order) {
      const _order = {
        field: order.replace('-', ''),
        sort: order.includes('-') ? 'asc' : 'desc'
      };
      return { ...props.initialState, sorting: { sortModel: [{ field: _order.field, sort: _order.sort }] } };
    }
    return props.initialState || {};
  };

  const handleOnRowDoubleClick = (params: GridRowParams) => {
    if (typeof props.onRowDoubleClick === 'function') {
      props.onRowDoubleClick(params);
    }
  };

  const handleColumnOrderOnChange = () => {
    if (!!flags.shouldAccountUseHistoryData) {
      const cols = apiRef.current
        .getAllColumns()
        .filter((item: any) => !item.hide && item.field !== '__check__')
        .map((col: any) => col.field);

      queryBuilder.setBaseURL(initialPath);
      queryBuilder.buildQuery({ filters: filters, search: search, order: '', fields: cols }, globalFilter);
      const { context, subContext } = getContextAndSubContext();
      dispatch(setHistoryData({ context, subContext, data: { url: queryBuilder.getQuery(), fields: cols } }));
      dispatch(setFields(cols));
      dispatch(setIsEdited(true));
    }
  };

  const columnsOrderProp = !!flags.shouldAccountUseHistoryData
    ? { onColumnOrderChange: handleColumnOrderOnChange }
    : {};

  return (
    <div className="grid" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="grid-wrapper">
        {props.showInfoPanel && !loading && (
          <div style={{ height: 40, display: 'flex', padding: 10, alignItems: 'center' }}>
            <img src="/assets/images/info.svg" className="info" alt="evidence info" />
            <Text style={{ paddingLeft: 10 }} textSize={13} textColor="#6d6d7a">
              IONIX employs advanced classification techniques and Machine Learning algorithms in the discovery process.
              The table below presents dominant features that attribute the domain to the organization.
            </Text>
          </div>
        )}
        <GridUtilities
          apiGrid={apiRef}
          isMoreButton={props.isMoreButton}
          isCSVDownloadable={props.isCSVDownloadable}
          isActionItemsPDFDownloadable={props.isActionItemsPDFDownloadable}
          selectionModel={selectionModel}
          buttonSelectionText={props.buttonSelectionText}
          elementSelect={props.elementSelect}
          loadedRows={rows}
          columns={props.columns}
          actionMenuOnChange={props.actionMenuOnChange}
          downloadCSVPath={csvDownloadPath}
          addedActions={props.addedActions}
          identifier={props.identifier}
          page={props.page}
          pageTab={props.pageTab}
          savedViews={props.savedViews}
          isClusterSupported={props.isClusterView}
          clusterType={clusterType}
          onToggleCluster={(type: string) => {
            dispatch(setClusterType(type));
          }}
          getContextAndSubContext={getContextAndSubContext}
          enableLocalSearch={props.clientSideMode}
          onLocalSearch={handleLocalSearch}
        />
        <div className="grid-scope">
          <div
            style={{ height: '100%', width: '100%', background: '#FFFFFF', position: 'relative', display: 'flex' }}
            data-testid="data-grid-pro"
          >
            {flags.isSavedViews && (apiRef.current || !!clusterType) && (
              <SavedViews
                source="grid"
                open={isShowSavedViews}
                apiGrid={apiRef}
                columns={updatedColumns}
                page={props.page}
                tab={props.pageTab}
                onBackToDefault={handleUrlChange}
                getContextAndSubContext={getContextAndSubContext}
              />
            )}
            <div style={{ position: 'relative', flex: 1 }}>
              {loading && <AppLoading />}

              <RowCount
                isClusterView={!!clusterType}
                conut={getDisplayCount()}
                typeData={'Assets'}
                selectedCount={selectionModel.length}
              />

              {!!clusterType ? (
                <ClusterGrid columns={updatedColumns} type={clusterType} onSelection={handleOnSelectionModelChange} />
              ) : (
                <StyledDataGridPro
                  components={{
                    NoRowsOverlay: () => <ManageNotData />
                  }}
                  apiRef={apiRef}
                  rows={props.clientSideMode ? filteredRows : _getRows()}
                  columns={updatedColumns}
                  onRowDoubleClick={handleOnRowDoubleClick}
                  onSortModelChange={handleOnSortModelChange}
                  onRowsScrollEnd={handleOnRowsScrollEnd}
                  onColumnResize={handleOnColumnResize}
                  onSelectionModelChange={handleOnSelectionModelChange}
                  selectionModel={selectionModel}
                  filterMode="server"
                  sortingMode={props.clientSideMode ? "client" : "server"}
                  disableMultipleColumnsSorting={true}
                  disableSelectionOnClick={true}
                  checkboxSelection
                  disableColumnMenu={true}
                  hideFooter={true}
                  getRowId={row => row[props.identifier]}
                  initialState={getOrder()}
                  // onColumnOrderChange={handleColumnOrderOnChange}
                  disableColumnReorder={!flags.shouldAccountUseHistoryData}
                  {...columnsOrderProp}
                />
              )}
            </div>

            {/* {!!showSelectedGridMenu && !!selectionModel && !!selectionModel.length && !isPopSideOpen && (
              <SelectedGridRowsMenu
                selectedRows={selectionModel}
                unselectAllRows={unselectAllRows}
                onClose={() => setShowSelectedGridMenu(false)}
                actionsOptions={props.floatingMenuOptions}
                identifier={props.identifier}
                loadedRows={rows}
                actionMenuOnChange={props.actionMenuOnChange}
                apiGrid={apiRef}
              />
            )} */}

            <InfoPanel open={isOpenInfoPanel} />
          </div>
        </div>
      </div>
    </div>
  );
}
